import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  CreditCard, 
  Video, 
  ClipboardList,
  Calendar,
  Phone,
  Download,
  ExternalLink,
  CheckCircle2,
  Clock,
  Shield
} from 'lucide-react';

const resources = [
  {
    icon: ClipboardList,
    title: 'New Patient Forms',
    description: 'Download and complete your new patient forms before your first visit to save time.',
    items: [
      'Patient Registration Form',
      'Medical History Questionnaire',
      'HIPAA Privacy Notice',
      'Insurance Information Form'
    ]
  },
  {
    icon: CreditCard,
    title: 'Insurance & Billing',
    description: 'Information about accepted insurance plans and billing procedures.',
    items: [
      'We accept most major insurance plans',
      'Medicare and Medicaid accepted',
      'Workers\' Compensation cases welcome',
      'Flexible payment options available'
    ]
  },
  {
    icon: Video,
    title: 'Telemedicine Services',
    description: 'Virtual consultations available for new and existing patients.',
    items: [
      'HIPAA compliant VSee software',
      'Ideal for follow-up appointments',
      'Second opinions for out-of-area patients',
      'After-hours consultations available'
    ]
  },
  {
    icon: FileText,
    title: 'Patient Portal',
    description: 'Access your medical records, test results, and communicate with our team.',
    items: [
      'View appointment history',
      'Access test results',
      'Message your care team',
      'Request prescription refills'
    ]
  }
];

const prepareItems = [
  'Bring your photo ID and insurance card',
  'Bring a list of current medications',
  'Bring any relevant X-rays or MRI images',
  'Arrive 15 minutes early to complete paperwork',
  'Wear comfortable, loose-fitting clothing',
  'Bring a list of questions for your doctor'
];

const acceptedInsurance = [
  'Medicare',
  'Blue Cross Blue Shield',
  'Aetna',
  'Cigna',
  'United Healthcare',
  'Humana',
  'Tricare',
  'Workers Compensation',
  'Most PPO and HMO plans'
];

const PatientResourcesPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-hero py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <FileText className="w-4 h-4" />
            <span className="text-sm font-semibold">Patient Resources</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Patient Resources
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Everything you need to prepare for your visit and make the most of your care at Melbourne Hand Center.
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {resources.map((resource) => (
              <Card key={resource.title} className="bg-card border-0 shadow-card hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <resource.icon className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-semibold text-foreground mb-2">{resource.title}</h3>
                      <p className="text-muted-foreground">{resource.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {resource.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Prepare for Your Visit */}
      <section className="py-16 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-semibold">Before Your Visit</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Prepare for Your Appointment
              </h2>
              <p className="text-muted-foreground mb-8">
                To ensure your visit goes smoothly and we can provide you with the best care possible, 
                please follow these guidelines before your appointment.
              </p>
              <ul className="space-y-4">
                {prepareItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-semibold text-primary">{index + 1}</span>
                    </div>
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="bg-card border-0 shadow-xl">
              <CardContent className="p-8">
                <h3 className="font-display text-2xl font-bold text-foreground mb-6">
                  Schedule Your Visit
                </h3>
                <p className="text-muted-foreground mb-6">
                  Ready to schedule your appointment? We offer same-day appointments and telemedicine 
                  options for your convenience.
                </p>
                <div className="space-y-4">
                  <Button variant="hero" size="lg" className="w-full" asChild>
                    <a 
                      href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      Schedule Online
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full" asChild>
                    <a href="tel:321-500-4263">
                      <Phone className="w-5 h-5 mr-2" />
                      Call 321.500.4263
                    </a>
                  </Button>
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground text-center">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Office Hours: Mon-Fri, 8:00 AM - 5:00 PM
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-semibold">Insurance</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Accepted Insurance Plans
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We accept most major insurance plans. If you don't see your insurance listed, 
              please contact us to verify coverage.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {acceptedInsurance.map((insurance) => (
              <span 
                key={insurance}
                className="px-6 py-3 bg-card text-foreground rounded-xl text-sm font-medium shadow-card border border-border"
              >
                {insurance}
              </span>
            ))}
          </div>
          <p className="text-center text-muted-foreground">
            Questions about insurance or billing?{' '}
            <a href="tel:321-500-4263" className="text-primary hover:underline font-medium">
              Call us at 321.500.4263
            </a>
          </p>
        </div>
      </section>

      {/* Telemedicine Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary-foreground/10 text-primary-foreground px-4 py-2 rounded-full mb-6">
                <Video className="w-4 h-4" />
                <span className="text-sm font-semibold">Virtual Care</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mb-6">
                Telemedicine Appointments
              </h2>
              <p className="text-primary-foreground/90 mb-6">
                We understand that it can be difficult to schedule and attend a medical appointment. 
                That's why we offer telemedicine appointments for patients who prefer this option.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>HIPAA compliant VSee software for secure communication</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Ideal for new patients with urgent issues</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Perfect for out-of-area patients seeking second opinions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>After-hours consultations available</span>
                </li>
              </ul>
              <Button variant="white" size="lg" asChild>
                <a href="tel:321-500-4263">
                  <Phone className="w-5 h-5 mr-2" />
                  Call to Schedule Telemedicine
                </a>
              </Button>
            </div>
            <div className="bg-primary-foreground/10 rounded-2xl p-8">
              <h3 className="font-display text-xl font-bold mb-4">How Telemedicine Works</h3>
              <ol className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Schedule Your Appointment</p>
                    <p className="text-sm text-primary-foreground/80">Call or email us to book your telemedicine visit</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Receive Your Link</p>
                    <p className="text-sm text-primary-foreground/80">We'll send you a secure VSee video link</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Connect with Your Doctor</p>
                    <p className="text-sm text-primary-foreground/80">Meet face-to-face from the comfort of your home</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PatientResourcesPage;
